import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format, parse } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Clock, Car, CalendarDays } from 'lucide-react';
import apiService from '../services/apiService';
import ServiceCalendar from './ServiceCalendar';
import StatusBadge from './ui/StatusBadge';
import AppointmentDetailModal from './ui/AppointmentDetailModal';

const DATE_FMT = 'yyyy-MM-dd';
const toDate = (dateStr) => parse(dateStr, DATE_FMT, new Date());
const todayStr = () => format(new Date(), DATE_FMT);

const Appointments = () => {
  const [selectedDateStr, setSelectedDateStr] = useState(todayStr);
  const [slots, setSlots] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [selectedApptId, setSelectedApptId] = useState(null);
  const [detailAppt, setDetailAppt] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const slotsCacheRef = useRef({});

  useEffect(() => {
    let cancelled = false;
    apiService.fetchAppointments()
      .then((apptData) => {
        if (!cancelled) setAllAppointments(Array.isArray(apptData) ? apptData : []);
      })
      .catch((e) => console.error(e));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedDateStr) return undefined;

    const cached = slotsCacheRef.current[selectedDateStr];
    if (cached) {
      setSlots(cached);
      setSlotsLoading(false);
    } else {
      setSlotsLoading(true);
    }

    let cancelled = false;
    apiService.fetchSlots(selectedDateStr)
      .then((slotData) => {
        if (cancelled) return;
        const nextSlots = slotData.slots || [];
        slotsCacheRef.current[selectedDateStr] = nextSlots;
        setSlots(nextSlots);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        if (!cancelled) setSlotsLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedDateStr]);

  const handleDaySelect = useCallback((dateStr) => {
    // dateStr is null when the panel closes — keep last selection visible in sidebar
    if (dateStr) {
      setSelectedDateStr(dateStr);
      setSelectedApptId(null);
    }
  }, []);

  const dayAppointments = useMemo(() => {
    if (!selectedDateStr) return [];
    const map = new Map();
    for (const a of allAppointments) {
      if (a.status === 'cancelled') continue;
      if (!map.has(a.date)) map.set(a.date, []);
      map.get(a.date).push(a);
    }
    return (map.get(selectedDateStr) || [])
      .slice()
      .sort((x, y) => (x.time || '').localeCompare(y.time || ''));
  }, [allAppointments, selectedDateStr]);

  const availableSlots = useMemo(() => slots.filter((s) => s.isAvailable), [slots]);

  const selectedDateObj = selectedDateStr ? toDate(selectedDateStr) : null;

  return (
    <div className="page-container">
      <div className="flex items-start gap-6">
        {/* Main calendar */}
        <div className="min-w-0 flex-1">
          <ServiceCalendar onDaySelect={handleDaySelect} />
        </div>

        {/* Sidebar — always mounted to avoid remount blink on day change */}
        {selectedDateObj && (
          <div className="w-72 shrink-0 space-y-4">
            {/* Selected day summary */}
            <div className={`card transition-opacity duration-150 ${slotsLoading ? 'opacity-60' : 'opacity-100'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Seçili gün</p>
                  <h3 className="mt-0.5 text-lg font-semibold capitalize leading-tight text-white">
                    {format(selectedDateObj, 'd MMMM', { locale: tr })}
                  </h3>
                  <p className="mt-0.5 truncate text-sm capitalize text-slate-400">
                    {format(selectedDateObj, 'EEEE', { locale: tr })}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end pt-5">
                  <span className="text-2xl font-bold leading-none text-white">{dayAppointments.length}</span>
                  <span className="text-[11px] text-slate-400">randevu</span>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-emerald-400/[0.07] p-3 ring-1 ring-emerald-400/20">
                  <p className="text-xs text-emerald-300">Müsait saat</p>
                  <p className="text-xl font-bold text-white">{availableSlots.length}</p>
                </div>
                <div className="rounded-xl bg-sky-400/[0.07] p-3 ring-1 ring-sky-400/20">
                  <p className="text-xs text-sky-300">Dolu / toplam</p>
                  <p className="text-xl font-bold text-white">
                    {slots.length ? `${slots.length - availableSlots.length}/${slots.length}` : '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Available slots grid */}
            <div className={`card transition-opacity duration-150 ${slotsLoading ? 'opacity-60' : 'opacity-100'}`}>
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
                <Clock className="h-4 w-4 text-emerald-300" /> Müsait saatler
              </h3>
              {slots.length === 0 && !slotsLoading ? (
                <p className="text-sm text-slate-500">Bu gün için servis saati bulunmuyor.</p>
              ) : slots.length === 0 && slotsLoading ? (
                <p className="text-sm text-slate-500">Yükleniyor…</p>
              ) : (
                <div className="grid max-h-44 grid-cols-3 gap-2 overflow-y-auto pr-1">
                  {slots.map((s) => (
                    <div
                      key={s.time}
                      className={`rounded-lg px-1 py-2 text-center text-sm ring-1 ${
                        s.isAvailable
                          ? 'bg-emerald-400/10 text-emerald-300 ring-emerald-400/25'
                          : 'bg-white/5 text-slate-500 ring-white/5'
                      }`}
                      title={s.isAvailable ? 'Müsait' : 'Dolu'}
                    >
                      {s.time}
                      <div className="text-[10px] opacity-80">
                        {s.booked}/{s.maxCapacity}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Booked appointments */}
            <div className="card">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
                <Car className="h-4 w-4 text-sky-300" /> Kayıtlı randevular ({dayAppointments.length})
              </h3>
              {dayAppointments.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/10 bg-white/[0.02] px-4 py-8 text-center">
                  <CalendarDays className="mb-3 h-8 w-8 text-slate-600" />
                  <p className="text-sm font-medium text-slate-300">Bu tarihte randevu yok</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Dijital asistan yeni randevu oluşturduğunda burada görünür.
                  </p>
                </div>
              ) : (
                <ul className="max-h-[40vh] space-y-3 overflow-y-auto pr-1">
                  {dayAppointments.map((a) => {
                    const pending = a.status === 'pending' || a.confirmed === false;
                    const active = a.id === selectedApptId;
                    return (
                      <li
                        key={a.id}
                        onClick={() => { setSelectedApptId(a.id); setDetailAppt(a); }}
                        className={`cursor-pointer rounded-xl p-3 text-sm ring-1 transition-all ${
                          active
                            ? 'bg-accent-400/[0.08] ring-accent-400/40'
                            : 'bg-white/[0.02] ring-white/10 hover:ring-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-white">{a.time} · {a.plate}</span>
                          <StatusBadge status={pending ? 'Appointment Requested' : 'Appointment Created'} />
                        </div>
                        <div className="mt-0.5 text-slate-200">{a.customerName}</div>
                        <div className="mt-1 text-slate-400">
                          {a.serviceType}
                          {a.kilometer ? ` · ${a.kilometer.toLocaleString('tr-TR')} km` : ''}
                        </div>
                        {a.additionalComplaints && (
                          <div className="mt-1 text-slate-500">Şikayet: {a.additionalComplaints}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      {detailAppt && (
        <AppointmentDetailModal appointment={detailAppt} onClose={() => setDetailAppt(null)} />
      )}
    </div>
  );
};

export default Appointments;
